/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable no-underscore-dangle */
/* eslint-disable @typescript-eslint/naming-convention */
/**
 * Copyright (c) 2021, Ouster, Inc.
 * All rights reserved.
 */
import {
  dateToDayString,
  dateTo24HrMinString,
} from '@ouster/webviz-react-components';

import {
  FilterList,
  ArrowDropUp,
  ArrowDropDown,
  Public,
} from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components/macro';

// eslint-disable-next-line import/no-cycle
import {
  ArchitectSceneFilter,
  FilterContainer,
  ResetFiltersButton,
} from './ArchitectSceneFilter';
import { PaginationControls } from './PaginationControls';
import { SVGIcon } from './SVGIconButton';
import { useStoreState } from '../reducers';
import {
  ArchitectSceneInfo,
  ArchitectSceneTableColumn,
  PaginationInfo,
  OrderableField,
  ArchitectFilterableField,
  ArchitectFilterState,
  LoadStatus,
} from '../types';
import { setOrderFieldInUrl } from '../utils/searchParams';

// enum DateType {
//   UPLOADED,
//   RECORDED,
// }

const FILTER_FIELDS_BY_COLUMN: {
  col: ArchitectSceneTableColumn;
  keys: ArchitectFilterableField[];
}[] = [
  {
    col: ArchitectSceneTableColumn.TAGS,
    keys: [ArchitectFilterableField.tags],
  },
  {
    col: ArchitectSceneTableColumn.CREATOR,
    keys: [ArchitectFilterableField.created_by],
  },
];

const ORDER_FIELDS_BY_COLUMN: Map<ArchitectSceneTableColumn, OrderableField> =
  new Map<ArchitectSceneTableColumn, OrderableField>([
    [ArchitectSceneTableColumn.CREATED_DATE, OrderableField.created_at_dsc],
  ]);

const COLUMN_NAMES: Map<ArchitectSceneTableColumn, string> = new Map([
  [ArchitectSceneTableColumn.TITLE, 'Title'],
  [ArchitectSceneTableColumn.CREATED_DATE, 'Created'],
  [ArchitectSceneTableColumn.CREATOR, 'Creator'],
  [ArchitectSceneTableColumn.TAGS, 'Tags'],
]);

const fieldsForCol = (col: ArchitectSceneTableColumn) =>
  FILTER_FIELDS_BY_COLUMN.filter((colInfo) => colInfo.col === col).reduce(
    (acc, vals) => [...acc, ...vals.keys],
    [] as ArchitectFilterableField[]
  );

const TableContainer = styled.div`
  /* Hack since overflow-x:visible is not working */
  /* https://stackoverflow.com/questions/6421966/css-overflow-x-visible-and-overflow-y-hidden-causing-scrollbar-issue */
  // TODO(pierre) on 2021-05-06: Move to a var
  --hack-left-padding: 56px;
  padding-left: var(--hack-left-padding);
  margin-left: calc(var(--hack-left-padding) * -1);

  width: calc(100% + var(--hack-left-padding));

  /* Make it shrink to the page and scrollable */
  flex: 1 1 auto;
  overflow-y: scroll;
  /* Override min-width of auto so it can shrink */
  min-height: 450px;

  /* Remove scrollbar to avoid distraction here */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE */
  ::-webkit-scrollbar {
    width: 0; /* Chrome and Safari */
  }
`;

// Specify column sizes and make the last tag column expand as needed,
// while keeping a min size of 56px to prevent pagination controls from
// overlapping with it on smaller screens
const sdkMainTemplateColumns = '180px 180px 180px minmax(56px,1fr)';
// Add Pagination controls to the header row
const sdkHeaderTemplateColumns = [sdkMainTemplateColumns, 'auto'].join(' ');

const rowCss = css<{ gridtemplatecolumns: string }>`
  width: 100%;
  min-width: max-content;
  padding-right: 2rem;

  display: grid;
  grid-template-columns: ${(props) => props.gridtemplatecolumns};
  align-items: center;
  column-gap: 25px;

  font-size: 12px;
`;

const PublishedStatus = styled.div`
  /* Align to the right of a row */
  position: absolute;
  right: 0;

  /* Make sure other elements (i.e. tags list) are hidden underneath */
  height: 100%;
  padding-left: 2px;
  padding-right: 6px;

  /* Center the link inside of it */
  display: grid;
  place-items: center;

  /* Make the link look like a button. We don't use a button element
  here though because on hover we want the browser to display a tooltip
  with the link about to be opened. This gives better feedforward */
  & > a {
    border-radius: 2px;
    padding: 6px;

    /* Place icon in the center */
    display: grid;
    place-items: center;

    ${SVGIcon} {
      // Custom grey which doesn't draw the attention too much
      color: #c0c0c0;
    }
    &:hover {
      background-color: transparent;
    }
  }

  /* Style the link differently on hover to give feedforward to the user
  that they will open a different link than the rest of the row */
  &:hover > a {
    ${SVGIcon} {
      color: var(--color-blast-off-blue);
    }
  }
`;

const Row = styled(Link)<{ gridtemplatecolumns: string }>`
  ${rowCss}

  /* Add the processing status on the right side */
  position: relative;
  padding-left: 4px;
  padding-block: 2px;
  grid-template-rows: repeat(auto-fill, 41px);

  color: var(--color-gray-3);
  border-bottom: 1px solid var(--color-gray-7);

  &:hover {
    background-color: #ffffff30;
    cursor: pointer;

    /* Override Link color */
    color: white;
  }
`;

const HeaderRow = styled.div<{ gridtemplatecolumns: string }>`
  ${rowCss}

  grid-template-rows: 29px;

  border-bottom: 2px solid var(--color-galaxy-gray);
  background: var(--color-gray-11);

  /* Keep header always visible */
  position: sticky;
  top: 0;
  z-index: var(--z-sticky);
`;

const RowCell = styled.div`
  /* Keep everything on one line */
  white-space: nowrap;
`;

// Wrap header cell inside a relative container so some column titles
// can be offseted to avoid overlap
const HeaderCellContainer = styled.div`
  position: relative;
  height: 100%;
`;

const SpinnerContainer = styled.div`
  height: 50vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledHeaderText = styled.div``;

// Add a relative container around the header icon so we can move it up
// by 1px and align it with the uppercase header title better
const HEADER_ICON_SIZE = '20px';
const HeaderIconContainer = styled.div`
  width: ${HEADER_ICON_SIZE};
  height: ${HEADER_ICON_SIZE};

  position: relative;

  ${SVGIcon} {
    position: absolute;
    top: -1px;
  }
`;

// Separate FilterIcon and SortingIcon because they have different hover behavior
const FilterIcon = styled(SVGIcon)``;
const SortingIcon = styled(SVGIcon)``;

const StyledHeaderCell = styled.div<{
  leftOffset?: string;
  filterable: boolean;
  filtered: boolean;
  sortable: boolean;
  sorted: boolean;
}>`
  /* Reset button style */
  all: unset;
  ${(props) => (props.filterable || props.sortable) && 'cursor: pointer;'};

  /* Absolute to be given an offset */
  position: absolute;
  ${(props) => (props.leftOffset ? `left: ${props.leftOffset};` : '')};
  /* More predictable with the abs positioning. */
  height: 100%;

  /* Flex container for header text and filter icon */
  display: flex;
  align-items: center;
  /* Dropdown arrow for sorting is smaller and looks better without a
  gap */
  ${(props) => props.filterable && 'gap: 4px;'}
  overflow-x: visible;

  /* Make header blue if some filters are selected */
  ${StyledHeaderText} {
    color: ${(props) =>
      props.filtered ? 'var(--color-blast-off-blue)' : 'var(--color-gray-2)'};
  }
  ${FilterIcon} {
    color: ${(props) =>
      props.filtered ? 'var(--color-blast-off-blue)' : 'var(--color-gray-5)'};
  }

  ${SortingIcon} {
    color: ${(props) =>
      props.sorted ? 'var(--color-gray-5)' : 'var(--color-gray-2)'};
  }

  &:hover {
    ${SortingIcon} {
      ${(props) => !props.sorted && `color: var(--color-gray-5);`}
    }
  }

  &:focus-within {
    /* Make header blue when focused */
    ${StyledHeaderText} {
      color: var(--color-blast-off-blue);
    }
    ${FilterIcon} {
      color: var(--color-blast-off-blue);
    }

    /* Reveal filter container when focused */
    ${FilterContainer} {
      display: flex;
    }
  }
`;

const GridWithDelimiters = styled.div<{ gridtemplatecolumns: string }>`
  display: grid;
  grid-template-columns: ${(props) => props.gridtemplatecolumns};
  gap: 5px;
`;

const StyledDelimiter = styled.div`
  &::after {
    color: var(--color-gravity-gray);
    content: '|';
  }
`;

// Is a given column currently filtered?
const isFiltered = (
  filterState: ArchitectFilterState | undefined,
  col: ArchitectSceneTableColumn
) => {
  if (filterState === undefined) return false;

  const columnKeys = fieldsForCol(col);
  const numActiveColFilters = filterState
    .filter((filterSection) =>
      columnKeys.includes(filterSection.sectionInfo.filterField)
    )
    .reduce((acc, filterSection) => acc + filterSection.values.length, 0);
  return numActiveColFilters > 0;
};
// Is a column filterable
const isFilterable = (col: ArchitectSceneTableColumn) => {
  if (fieldsForCol(col).length > 0) {
    return true;
  }
  return false;
};

// Is a column sortable
const isSortable = (col: ArchitectSceneTableColumn) =>
  ORDER_FIELDS_BY_COLUMN.has(col);
const isSortAsc = (col: ArchitectSceneTableColumn) => {
  if (ORDER_FIELDS_BY_COLUMN.get(col) === OrderableField.created_at_asc) {
    return true;
  }
  return false;
};

// Flip the sorting field
const flipSort = (orderField: OrderableField) => {
  if (orderField === OrderableField.created_at_asc) {
    orderField = OrderableField.created_at_dsc;
  } else {
    orderField = OrderableField.created_at_asc;
  }
  ORDER_FIELDS_BY_COLUMN.set(
    ArchitectSceneTableColumn.CREATED_DATE,
    orderField
  );
  return orderField;
};

// Are we sorting by a given column
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
const isSorted = (col: ArchitectSceneTableColumn, sortedBy?: OrderableField) =>
  true;

const HeaderCell: React.FC<{
  column: ArchitectSceneTableColumn;
  width?: string;
  leftOffset?: string;
}> = ({ column, leftOffset, width = '224px' }) => {
  const inputHandle = useRef<HTMLInputElement | null>(null);
  const { activeFilters, orderByField } = useStoreState((storeState) => ({
    ...storeState.architectSceneList,
  }));

  return (
    <HeaderCellContainer>
      <StyledHeaderCell
        filterable={isFilterable(column)}
        sortable={isSortable(column)}
        filtered={isFiltered(activeFilters, column)}
        sorted={isSorted(column, orderByField)}
        leftOffset={leftOffset || undefined}
        // Give the cell the role of a button without being a button
        // (because we have other nested buttons below)
        role={isFilterable(column) ? 'button' : ''}
        aria-pressed="false"
        tabIndex={isFilterable(column) ? 0 : undefined}
        onClick={() => {
          // Allow user to start typing as soon as the header is clicked
          if (isFilterable(column) && inputHandle.current) {
            inputHandle.current.focus();
            return;
          }

          // TODO(pierre) on 2021-06-23: It would also be nice to hide
          // the filter popup when the user clicks on the header again.
          // This would require knowing whether or not the popup was
          // revealed, but this is not that straightforward: we use
          // focus-within in CSS to reveal it so by the time onClick is
          // triggered, the popup is already revealed. We can't rely on
          // whether the input is already focused or not either, because
          // onFocus will be triggered first on this StyleHeaderCell,
          // and the input has already lost focus. One solution might be
          // to have the hiding logic in onMouseDown before the input
          // loses focus, but because onMouseDown bubbles, it's a bit
          // tricky to be sure that the user wasn't just adding or
          // selecting filters in the popup. It might be doable to do
          // specific checks on onMouseDown event targets, but that
          // would be a bit hacky and we'll leave this for a later time.

          // Change the ordering of the drive list if this is a sortable
          // column
          const _orderByField = ORDER_FIELDS_BY_COLUMN.get(column);
          if (isSortable(column) && _orderByField) {
            setOrderFieldInUrl(flipSort(_orderByField));
          }
        }}
      >
        <StyledHeaderText className="title">
          {COLUMN_NAMES.get(column)}
        </StyledHeaderText>
        {isFilterable(column) && (
          <>
            <HeaderIconContainer>
              <FilterIcon size={HEADER_ICON_SIZE}>
                <FilterList />
              </FilterIcon>
            </HeaderIconContainer>
            <ArchitectSceneFilter
              width={width}
              column={column}
              setInputHandle={(input) => {
                inputHandle.current = input;
              }}
              fields={fieldsForCol(column)}
            />
          </>
        )}
        {isSortable(column) && (
          <HeaderIconContainer>
            <SortingIcon size={HEADER_ICON_SIZE}>
              {isSortAsc(column) && <ArrowDropDown />}
              {!isSortAsc(column) && <ArrowDropUp />}
            </SortingIcon>
          </HeaderIconContainer>
        )}
      </StyledHeaderCell>
    </HeaderCellContainer>
  );
};

const DateCellContent: React.FC<{ architect_scenes: ArchitectSceneInfo }> = ({
  architect_scenes,
}) => {
  let dateString: Date | null = null;
  dateString = architect_scenes.createdAt;

  return (
    dateString && (
      <GridWithDelimiters gridtemplatecolumns="73px 3px 34px">
        <div>{dateToDayString(dateString)}</div>
        <StyledDelimiter />
        <div>{dateTo24HrMinString(dateString)}</div>
      </GridWithDelimiters>
    )
  );
};

const tagString = (tags: string[]) => tags.sort().join(', ');

// const revString = (revCode: string | null) => {
//   return revCode ? `Rev ${revCode}` : '';
// };

// TODO(pierre) on 2021-05-11: Use React.Memo?
const ArchitectSceneRow: React.FC<{ architect_scenes: ArchitectSceneInfo }> = ({
  architect_scenes,
}) => (
  <Row
    gridtemplatecolumns={sdkMainTemplateColumns}
    to={`/architect/${architect_scenes.id}`}
    target="_blank"
    rel="noopener noreferrer"
  >
    <RowCell>
      <DateCellContent architect_scenes={architect_scenes} />
    </RowCell>
    <RowCell className="ellipsis">{architect_scenes.createdBy}</RowCell>
    <RowCell className="ellipsis">{architect_scenes.title}</RowCell>
    <RowCell className="horizontalScroll">
      {tagString(architect_scenes.tags)}
    </RowCell>
    {/* We only display the world icon when the data is published
          because the lock icon for private data was a bit distracting */}
    {architect_scenes.publicURL && (
      <PublishedStatus>
        <a target="_blank" rel="noreferrer" href={architect_scenes.publicURL}>
          <SVGIcon size="20px">
            <Public />
          </SVGIcon>
        </a>
      </PublishedStatus>
    )}
  </Row>
);

const ArchitectSceneHeader: React.FC<object> = () => (
  <HeaderRow gridtemplatecolumns={sdkHeaderTemplateColumns}>
    <HeaderCell column={ArchitectSceneTableColumn.CREATED_DATE} />
    <HeaderCell column={ArchitectSceneTableColumn.CREATOR} />
    <HeaderCell column={ArchitectSceneTableColumn.TITLE} />
    <HeaderCell column={ArchitectSceneTableColumn.TAGS} width="260px" />

    <ResetFiltersButton />
  </HeaderRow>
);

const ArchitectSceneTable: React.FC<{
  architect_scenes: ArchitectSceneInfo[];
  paginationInfo: PaginationInfo;
  onPageChange: (nextPage: boolean) => void;
  loadStatus: LoadStatus;
}> = ({ architect_scenes, paginationInfo, onPageChange, loadStatus }) => (
  <>
    <TableContainer>
      <ArchitectSceneHeader />
      {loadStatus === 'loading' && (
        <SpinnerContainer aria-label="loading recording list viewer">
          <CircularProgress size="50px" thickness={2} />
        </SpinnerContainer>
      )}
      {loadStatus === 'failed' && (
        <div>Failed to load dataset. Please refresh to try again.</div>
      )}
      {loadStatus === 'success' &&
        architect_scenes.map((d) => (
          <ArchitectSceneRow architect_scenes={d} key={d.id} />
        ))}
    </TableContainer>
    {architect_scenes.length > 0 && loadStatus === 'success' && (
      <PaginationControls
        paginationInfo={paginationInfo}
        onPageChange={onPageChange}
      />
    )}
  </>
);

export { ArchitectSceneTable };
